import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PackagePlus, CheckCircle2, XCircle, Loader2, RefreshCcw, Image as ImageIcon, FileSpreadsheet } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: 'pending',  label: 'Pending'  },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all',      label: 'All'      },
];

function statusBadge(status) {
  const map = {
    pending:  'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${map[status] || 'bg-slate-100'}`}>{status}</span>;
}

function fullImg(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function ProductRequestsAdmin() {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewingId, setReviewingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  async function downloadExcel() {
    setExporting(true);
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`${API}/admin/product-requests/export.xlsx`, {
        params: { status: filter },
        headers: { Authorization: `Bearer ${tok}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `product-requests-${filter || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Export failed';
      toast.error(typeof msg === 'string' ? msg : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  function load(status = filter) {
    setLoading(true);
    const tok = localStorage.getItem('token');
    axios.get(`${API}/admin/product-requests`, {
      params: { status },
      headers: { Authorization: `Bearer ${tok}` },
    }).then((r) => {
      setItems(r.data.items || []);
      setPendingCount(r.data.pending_count || 0);
    }).catch(() => toast.error('Failed to load product requests'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function review(req, action) {
    const promptText = action === 'approve'
      ? `Approve "${req.product_name}"?\n\nAfter approval, the team can add it to the Master Catalogue.\nOptional note for the franchise:`
      : `Reject "${req.product_name}"?\n\nReason (will be shown to the franchise):`;
    const notes = window.prompt(promptText, '');
    if (notes === null) return;
    setReviewingId(req.id);
    try {
      const tok = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/product-requests/${req.id}/${action}`,
        { admin_notes: notes },
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'}`);
      // Update locally + refresh counts
      setItems((prev) => prev.map((p) => (p.id === req.id ? res.data : p)));
      load(filter);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Action failed';
      toast.error(typeof msg === 'string' ? msg : 'Action failed');
    } finally {
      setReviewingId(null);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <PackagePlus className="w-5 h-5 text-orange-600" />
          Product Requests from Stores
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-800 text-xs font-bold rounded-full px-2 py-0.5">
              {pendingCount} pending
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500">
          Franchises submit product proposals from their Store Portal. Approve or reject — approved
          items still need to be added to the Master Catalogue manually.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex gap-1">
            {STATUS_FILTERS.map((f) => (
              <Button key={f.value} size="sm"
                      variant={filter === f.value ? 'default' : 'outline'}
                      onClick={() => setFilter(f.value)}
                      data-testid={`req-filter-${f.value}`}>
                {f.label}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => load(filter)}
                    data-testid="req-refresh">
              <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExcel}
                    disabled={exporting || items.length === 0}
                    data-testid="req-export-excel">
              {exporting
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <FileSpreadsheet className="w-4 h-4 mr-1" />}
              {exporting ? 'Exporting...' : 'Download Excel'}
            </Button>
            <CsvDownloadButton onDownload={downloadExcel} testId="req-export-csv" disabled={items.length === 0} />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-400">No requests in this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Image</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-left px-3 py-2">Gramage</th>
                  <th className="text-right px-3 py-2">MRP</th>
                  <th className="text-right px-3 py-2">Requested ₹</th>
                  <th className="text-left px-3 py-2">From</th>
                  <th className="text-left px-3 py-2">Submitted</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isPending = r.status === 'pending';
                  const busy = reviewingId === r.id;
                  return (
                    <tr key={r.id} className="border-t" data-testid={`req-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <div className="w-12 h-12 rounded-md bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center">
                          {r.image_url ? (
                            <img src={fullImg(r.image_url)} alt={r.product_name}
                                 className="w-full h-full object-cover"
                                 onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium">{r.product_name}</td>
                      <td className="px-3 py-2">{r.gramage}</td>
                      <td className="px-3 py-2 text-right font-mono">₹{Number(r.mrp).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">
                        {r.requested_price != null ? `₹${Number(r.requested_price).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <p className="font-medium">{r.franchise_name}</p>
                        <p className="text-slate-400 font-mono">{r.franchise_id}</p>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {(r.created_at || '').split('T')[0]}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {statusBadge(r.status)}
                        {r.admin_notes && (
                          <p className="text-[10px] text-slate-500 mt-1 italic max-w-[160px] mx-auto">{r.admin_notes}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isPending ? (
                          <div className="inline-flex gap-1">
                            <Button size="sm" disabled={busy}
                                    className="bg-green-600 hover:bg-green-700 text-white h-7 px-2"
                                    onClick={() => review(r, 'approve')}
                                    data-testid={`req-approve-${r.id}`}>
                              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              <span className="ml-1 text-xs">Approve</span>
                            </Button>
                            <Button size="sm" variant="outline" disabled={busy}
                                    className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2"
                                    onClick={() => review(r, 'reject')}
                                    data-testid={`req-reject-${r.id}`}>
                              <XCircle className="w-3 h-3" />
                              <span className="ml-1 text-xs">Reject</span>
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">
                            by {r.reviewed_by_name || '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
